'use client';

import type { ForwardedRef, PropsWithChildren, ReactElement } from 'react';
import React, { useRef } from 'react';
import { mergeProps } from 'react-aria';
import { Spinner } from 'components/Spinner';
import { useToggleButtonContext } from 'components/ToggleButtonProvider';
import styled, { css } from 'styled-components';
import { mergeRefs } from '@react-aria/utils';
import type { IconProps } from '@frameio/vapor-icons';
import { theme } from '@frameio/vapor-theme';
import type { AddDollarPrefix } from '../../utils/types';
import { textStyles } from '../Text/text-variants';
import styles from './Button.module.css';
import { ButtonIcon } from './components/ButtonIcon';
import { isValidButtonProp } from './components/is-valid-button-prop';
import { disablePendingProps } from './isPending';
import { stripHandledEvents } from './stripHandledEvents';
import type { UseButtonProps } from './useButton';
import { useButton } from './useButton';

/**
 * Sizes
 */

export const TEXT_PADDING = 4;

export const buttonPaddings = {
  small: {
    x: 8,
    y: 2,
  },
  regular: {
    x: 8,
    y: 4,
  },
} as const;

export const buttonBoxSizes = {
  small: 24,
  regular: 32,
} as const;

export const sizes = {
  small: css`
    ${textStyles.linkSmall};
    height: ${theme.spaces[buttonBoxSizes.small]};
    line-height: ${theme.spaces[buttonBoxSizes.small]};
    padding: ${theme.spaces[buttonPaddings.small.y]}
      ${theme.spaces[buttonPaddings.small.x]};
  `,
  regular: css`
    ${textStyles.link};
    height: ${theme.spaces[buttonBoxSizes.regular]};
    line-height: ${theme.spaces[buttonBoxSizes.regular]};
    padding: ${theme.spaces[buttonPaddings.regular.y]}
      ${theme.spaces[buttonPaddings.regular.x]};
  `,
} as const;

export type ButtonSize = keyof typeof sizes;

/**
 * Variants
 */

export const BUTTON_VARIANTS = {
  primary: () => {
    const tokens = theme.colors.components.button.primary;

    return css`
      background: ${tokens.background};
      color: ${tokens.text};
      &[data-hovered] {
        background: ${tokens.interactive.background.hover};
      }

      &[aria-expanded='true'],
      &[data-pressed] {
        background: ${tokens.interactive.background.pressed};
      }

      &:disabled,
      &[aria-disabled='true'] {
        background: ${tokens.interactive.background.disabled};
        color: ${theme.colors.text.interactive.disabled};
        cursor: not-allowed;

        & > svg {
          color: ${theme.colors.text.interactive.disabled};
        }
      }
    `;
  },
  secondary: () => {
    const tokens = theme.colors.components.button.secondary;

    return css`
      background: ${tokens.background};
      color: ${tokens.text};
      &[data-hovered] {
        background: ${tokens.interactive.background.hover};
      }

      &[aria-expanded='true'],
      &[data-pressed] {
        background: ${tokens.interactive.background.pressed};
      }

      &:disabled,
      &[aria-disabled='true'] {
        background: ${tokens.interactive.background.disabled};
        color: ${theme.colors.text.interactive.disabled};
        cursor: not-allowed;

        & > svg {
          color: ${theme.colors.text.interactive.disabled};
        }
      }
    `;
  },
  tertiary: () => {
    const { secondary, tertiary } = theme.colors.components.button;

    return css`
      background: transparent;
      color: ${secondary.text};
      &[data-hovered] {
        background: ${tertiary.interactive.background.hover};
      }

      &[aria-expanded='true'],
      &[data-pressed] {
        background: ${tertiary.interactive.background.pressed};
      }

      &:disabled,
      &[aria-disabled='true'] {
        background: transparent;
        color: ${theme.colors.text.interactive.disabled};
        cursor: not-allowed;

        & > svg {
          color: ${theme.colors.text.interactive.disabled};
        }
      }
    `;
  },
  secure: () => {
    const secure = theme.colors.components.button.secure;
    return css`
      background: ${secure.background};
      color: ${secure.text};
      &[data-hovered] {
        background: ${secure.interactive.background.hover};
      }

      &[aria-expanded='true'],
      &[data-pressed] {
        background: ${secure.interactive.background.pressed};
      }

      &:disabled,
      &[aria-disabled='true'] {
        background: ${secure.interactive.background.disabled};
        color: ${theme.colors.text.interactive.disabled};
        cursor: not-allowed;

        & > svg {
          color: ${theme.colors.text.interactive.disabled};
        }
      }
    `;
  },
  destructive: () => {
    const tokens = theme.colors.components.button.destructive;

    return css`
      background: ${tokens.background};
      color: ${tokens.text};
      &[data-hovered] {
        background: ${tokens.interactive.background.hover};
      }

      &[aria-expanded='true'],
      &[data-pressed] {
        background: ${tokens.interactive.background.pressed};
      }

      &:disabled,
      &[aria-disabled='true'] {
        background: ${tokens.interactive.background.disabled};
        color: ${theme.colors.text.interactive.disabled};
        cursor: not-allowed;

        & > svg {
          color: ${theme.colors.text.interactive.disabled};
        }
      }
    `;
  },
  toggle: () => {
    const { secondary } = theme.colors.components.button;
    const { disabled: disabledColor } = theme.colors.text.interactive;

    // order of these selectors matters
    return css`
      background-color: ${secondary.backgroundSolid};
      color: ${secondary.text};

      &[data-selected] {
        background-color: ${secondary.interactive.background.selected};
        color: ${secondary.text};

        &:disabled,
        &[aria-disabled='true'] {
          background-color: ${secondary.interactive.background.disabledSolid};
          color: ${disabledColor};

          & > svg {
            color: ${theme.colors.text.interactive.disabled};
          }
        }

        &[data-hovered] {
          background-color: ${secondary.interactive.background.selectedHover};
        }
      }

      &:not([data-selected]) {
        &[data-hovered],
        &[aria-expanded='true'] {
          background-color: ${secondary.interactive.background.hoverSolid};
        }

        &[data-pressed],
        &[aria-pressed='true'] {
          background-color: ${secondary.interactive.background.pressedSolid};
        }
      }

      // disabled should be last
      &:disabled,
      &[aria-disabled='true'] {
        background-color: ${secondary.interactive.background.disabledSolid};
        color: ${disabledColor};
        cursor: not-allowed;

        & > svg {
          color: ${theme.colors.text.interactive.disabled};
        }
      }
    `;
  },
} as const;

/**
 * Shapes
 */

const BUTTON_SHAPES = {
  rectangular: css`
    border-radius: ${theme.radii[6]};
  `,
  rounded: css`
    border-radius: 20px;
  `,
} as const;

export type ButtonShapes = keyof typeof BUTTON_SHAPES;

export type SharedButtonProps = PropsWithChildren<UseButtonProps> &
  Pick<React.HTMLAttributes<HTMLButtonElement>, 'tabIndex'>;

/**
 * Button Component
 */

type ButtonVariants =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'secure'
  | 'destructive';

type ButtonLoadingProps =
  | {
      /**
       * If true, displays a spinner and disables the button.
       * When present, the label for the loading state may be omitted,
       * defaulting to "Loading…".
       */
      isLoading?: boolean;
      /**
       * Accessible label for the loading state.
       * Required if isLoading is true and you want to override the default label.
       */
      loadingLabel?: string;
    }
  | {
      /**
       * If true, displays a spinner and disables the button.
       */
      isLoading: true;
      /**
       * Accessible label for the loading state. Required when isLoading is true.
       */
      loadingLabel: string;
    };

export type ButtonProps = SharedButtonProps &
  ButtonLoadingProps & {
    /** Render button as a full-width element */
    block?: boolean;
    /** Render an icon from '@frameio/vapor-icons' before children */
    iconAfter?: ReactElement<IconProps>;
    /** Render an icon from '@frameio/vapor-icons' after children */
    iconBefore?: ReactElement<IconProps>;
    /** Minimum width of the button */
    minWidth?: string;
    /** Maximum width of the button */
    maxWidth?: string;
    /** Alter the size of the Button. */
    size?: ButtonSize;
    /** Alter the shape of the Button. default: 'rectangular' */
    shape?: ButtonShapes;
    /** Controls the style and color of buttons */
    variant?: ButtonVariants;
    /** Whether to space the text and icons evenly */
    spaceBetween?: boolean;
    /** Additional class name to apply to the button */
    className?: string;
  };

function Button(
  props: ButtonProps,
  forwardedRef: ForwardedRef<HTMLButtonElement>
) {
  const ref = useRef(null);

  const {
    size = 'regular',
    variant: variantProp = 'secondary',
    shape = 'rectangular',
    block = false,
    iconBefore,
    iconAfter,
    isLoading = false,
    loadingLabel = `Loading…`,
    minWidth = '0px',
    maxWidth = '100%',
    children,
    elementType,
    spaceBetween = false,
    ...other
  } = disablePendingProps(props);

  const { isToggle } = useToggleButtonContext();

  const variant = isToggle ? 'toggle' : variantProp;

  const { buttonProps, hasSharedHover } = useButton(ref, {
    ...other,
    enableSharedHover: variant === 'tertiary' || variant === 'toggle',
    elementType,
  });

  return (
    <StyledButton
      ref={mergeRefs(ref, forwardedRef)}
      $maxWidth={maxWidth}
      $minWidth={minWidth}
      $shape={shape}
      $size={size}
      $variant={variant}
      aria-disabled={isLoading || undefined}
      aria-label={isLoading ? loadingLabel : undefined}
      as={elementType}
      data-block={block || undefined}
      data-loading={isLoading || undefined}
      data-shared-hover-enabled={hasSharedHover || undefined}
      data-space-between={spaceBetween || undefined}
      {...mergeProps(
        buttonProps,
        {
          className: styles.buttonReset,
        },
        stripHandledEvents(props)
      )}
    >
      {isLoading ? (
        <Spinner
          aria-label={loadingLabel}
          centered
          delay={0}
          isLoading={true}
          size="x-small"
          variant="brandOverlay"
        />
      ) : null}
      <>
        <ButtonIcon icon={iconBefore} size={size} />
        <ButtonText>{children}</ButtonText>
        <ButtonIcon icon={iconAfter} size={size} />
      </>
    </StyledButton>
  );
}

export const buttonReset = css`
  &[type='button'] {
    -webkit-appearance: none;
    appearance: none;
  }
  -webkit-appearance: none;
  -webkit-tap-highlight-color: transparent;
  appearance: none;
  background: none;
  border: none;
  box-shadow: none;
  box-sizing: border-box;
  cursor: pointer;
  padding: 0;
  text-align: center;
  text-decoration: none;
  user-select: none;
  white-space: nowrap;
`;

export const buttonTransitions = css`
  transition-duration: 0.15s;
  transition-property: background, color;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
`;

type StyledButtonProps = AddDollarPrefix<
  Pick<ButtonProps, 'size' | 'block' | 'shape'>
> & {
  $isLoading: boolean;
  $variant: ButtonVariants | 'toggle';
  $sharedHoverEnabled: boolean;
  $minWidth: string;
  $maxWidth: string;
};

export const StyledButton = styled.button.withConfig({
  shouldForwardProp: isValidButtonProp,
})<StyledButtonProps>`
  ${buttonTransitions};
  font-family: ${theme.fonts.body};
  margin: 0;
  align-items: center;
  display: inline-flex;
  justify-content: center;
  min-width: ${(p) => p.$minWidth};
  max-width: ${(p) => p.$maxWidth};
  outline: none;
  position: relative;
  vertical-align: middle;
  flex-shrink: 0;

  & > svg {
    flex-shrink: 0;
  }

  &:focus-visible,
  &[data-focus-visible] {
    outline: 1.5px solid ${theme.colors.border.interactive.focused};
    outline-offset: 1.5px;
  }

  &[data-loading] {
    cursor: default;
  }

  &[data-loading] > *:not([role='progressbar']) {
    opacity: 0;
  }

  ${(p) => p.$size && sizes[p.$size]};

  ${(p) => BUTTON_VARIANTS[p.$variant]};

  ${(p) => p.$shape && BUTTON_SHAPES[p.$shape]};

  &[data-shared-hover-enabled] {
    background: transparent !important;
    will-change: transform;
  }

  &[data-block] {
    display: flex;
    width: 100%;
  }

  &[data-loading] {
    line-height: 0;
  }

  &[data-space-between] {
    justify-content: space-between;
  }

  &[data-space-between] > svg:last-child {
    margin-inline-start: auto;
  }
`;

const ButtonText = styled.span`
  display: flex;
  min-width: 0;
  padding: 0 ${TEXT_PADDING}px;
`;

/**
 * A Button component which wraps react-aria's useButton hook.
 *
 * @see WAI-ARIA https://www.w3.org/WAI/ARIA/apg/patterns/button/
 * @see Docs     http://vapor.frame.io/components/button
 * @see Docs     https://react-spectrum.adobe.com/react-aria/useButton.html
 * @example
 * <Button onPress={log('pressed!')}>
 *   Hello world
 * </Button>
 */

const _Button = React.forwardRef(Button);
_Button.displayName = 'Button';
export { _Button as Button };
